// frontend/components/HrManagerTimeTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployeesList,
  fetchManagerTimeEntries,
  updateManagerTimeEntry,
  deleteManagerTimeEntry,
} from "../features/hr/hrThunks.js";
import { CalendarDays, Clock, Users, Pencil, Trash2 } from "lucide-react";
import "../styles/hr/hr-manager-time.css";

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

const formatTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTimeInputValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const formatDateLabel = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  return date.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

// Normaliza duration_hours y calcula si falta (igual que en el tab de empleado)
const getDurationHours = (entry) => {
  if (!entry) return null;

  if (entry.duration_hours != null) {
    const parsed = Number(entry.duration_hours);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (!entry.check_in || !entry.check_out) return null;

  const start = new Date(entry.check_in);
  const end = new Date(entry.check_out);
  const diffMs = end - start;

  if (!Number.isFinite(diffMs) || diffMs <= 0) return null;

  const hours = diffMs / (1000 * 60 * 60);
  return Number(hours.toFixed(2));
};

const sumHours = (entries) =>
  entries.reduce((total, entry) => {
    const h = getDurationHours(entry);
    return total + (h || 0);
  }, 0);

const getEntryEmployeeName = (entry) => {
  const employee = entry.employee || entry.employee_profile || {};
  const user = employee.user || {};
  const fullName = ((user.first_name || "") + " " + (user.last_name || "")).trim();
  return fullName || user.username || user.email || "—";
};

const HrManagerTimeTab = () => {
  const dispatch = useDispatch();

  // Rango por defecto: 1 del mes actual → hoy
  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [hasSearched, setHasSearched] = useState(false);

  // Edición de una fila
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editCheckInTime, setEditCheckInTime] = useState("");
  const [editCheckOutTime, setEditCheckOutTime] = useState("");

  // Modal eliminar (confirm)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Empleados (para el select)
  const employeesListRaw = useSelector((state) => state.hr.employeesList) || [];
  const loadingEmployeesList = useSelector((state) => state.hr.loadingEmployeesList);
  const errorEmployeesList = useSelector((state) => state.hr.errorEmployeesList);

  const employeesList = useMemo(() => {
    if (Array.isArray(employeesListRaw)) return employeesListRaw;
    if (employeesListRaw && Array.isArray(employeesListRaw.results)) return employeesListRaw.results;
    return [];
  }, [employeesListRaw]);

  // Fichajes manager
  const managerTimeEntriesRaw = useSelector((state) => state.hr.managerTimeEntries) || [];
  const loadingManagerTimeEntries = useSelector((state) => state.hr.loadingManagerTimeEntries);
  const errorManagerTimeEntries = useSelector((state) => state.hr.errorManagerTimeEntries);

  const managerTimeEntries = useMemo(() => {
    if (Array.isArray(managerTimeEntriesRaw)) return managerTimeEntriesRaw;
    if (managerTimeEntriesRaw && Array.isArray(managerTimeEntriesRaw.results)) {
      return managerTimeEntriesRaw.results;
    }
    return [];
  }, [managerTimeEntriesRaw]);

  const sortedTimeEntries = useMemo(() => {
    const list = [...managerTimeEntries];
    list.sort((a, b) => {
      const aKey = `${a.date || ""} ${a.check_in || ""}`;
      const bKey = `${b.date || ""} ${b.check_in || ""}`;
      return bKey.localeCompare(aKey);
    });
    return list;
  }, [managerTimeEntries]);

  const loadingUpdateTimeEntry = useSelector((state) => state.hr.loadingUpdateTimeEntry);
  const loadingDeleteTimeEntry = useSelector((state) => state.hr.loadingDeleteTimeEntry);
  const errorUpdateTimeEntry = useSelector((state) => state.hr.errorUpdateTimeEntry);
  const errorDeleteTimeEntry = useSelector((state) => state.hr.errorDeleteTimeEntry);

  // Cargar empleados al entrar
  useEffect(() => {
    if (!employeesList || employeesList.length === 0) {
      dispatch(fetchEmployeesList());
    }
  }, [dispatch, employeesList]);

  const selectedEmployee =
    employeesList.find((e) => e.id === Number(selectedEmployeeId)) || null;

  // Carga inicial → todos los fichajes del rango (sin filtrar por empleado)
  useEffect(() => {
    if (!hasSearched && startDate && endDate) {
      setHasSearched(true);
      dispatch(fetchManagerTimeEntries({ startDate, endDate }));
    }
  }, [dispatch, hasSearched, startDate, endDate]);

  const handleSearch = () => {
    if (!startDate || !endDate) return;

    const payload = { startDate, endDate };
    if (selectedEmployeeId) payload.employeeId = selectedEmployeeId;

    setHasSearched(true);
    dispatch(fetchManagerTimeEntries(payload));
  };

  // Métricas
  const { totalHours, totalDays, avgPerDay } = useMemo(() => {
    if (!managerTimeEntries || managerTimeEntries.length === 0) {
      return { totalHours: 0, totalDays: 0, avgPerDay: 0 };
    }

    const total = sumHours(managerTimeEntries);
    const daysSet = new Set(managerTimeEntries.map((entry) => entry.date).filter(Boolean));
    const daysCount = daysSet.size || 0;
    const avg = daysCount ? total / daysCount : 0;

    return { totalHours: total, totalDays: daysCount, avgPerDay: avg };
  }, [managerTimeEntries]);

  const safeTotalHours = Number(totalHours || 0);
  const safeAvgPerDay = Number(avgPerDay || 0);

  const rangeLabel = `${startDate} → ${endDate}`;

  // ---- Edición de fichajes ----
  const startEditEntry = (entry) => {
    setEditingEntryId(entry.id);
    setEditDate(entry.date || "");
    setEditCheckInTime(formatTimeInputValue(entry.check_in));
    setEditCheckOutTime(formatTimeInputValue(entry.check_out));
  };

  const cancelEdit = () => {
    setEditingEntryId(null);
    setEditDate("");
    setEditCheckInTime("");
    setEditCheckOutTime("");
  };

  const handleSaveEdit = (entryId) => {
    if (!editDate || loadingUpdateTimeEntry) return;

    const payload = { date: editDate };
    payload.check_in = editCheckInTime ? `${editDate}T${editCheckInTime}` : null;
    payload.check_out = editCheckOutTime ? `${editDate}T${editCheckOutTime}` : null;

    dispatch(updateManagerTimeEntry({ timeEntryId: entryId, payload })).then((action) => {
      if (action.type.endsWith("fulfilled")) cancelEdit();
    });
  };

  // ---- Delete modal ----
  const openDeleteModal = (entry) => {
    if (loadingDeleteTimeEntry) return;
    cancelEdit();
    setEntryToDelete(entry);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (loadingDeleteTimeEntry) return;
    setDeleteModalOpen(false);
    setEntryToDelete(null);
  };

  useEffect(() => {
    if (!deleteModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (loadingDeleteTimeEntry) return;
      closeDeleteModal();
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [deleteModalOpen, loadingDeleteTimeEntry]);

  const confirmDelete = () => {
    if (!entryToDelete?.id) return;
    if (loadingDeleteTimeEntry) return;

    dispatch(deleteManagerTimeEntry(entryToDelete.id)).then((action) => {
      if (action.type.endsWith("fulfilled")) closeDeleteModal();
    });
  };

  const employeeLabel = selectedEmployee
    ? (
        ((selectedEmployee.user?.first_name || "") +
          " " +
          (selectedEmployee.user?.last_name || "")).trim() ||
        selectedEmployee.user?.username ||
        "Sin nombre"
      )
    : "Todos los empleados";

  const errorEmployeesText = getErrorText(errorEmployeesList);
  const errorManagerTimeEntriesText = getErrorText(errorManagerTimeEntries);
  const errorUpdateTimeEntryText = getErrorText(errorUpdateTimeEntry);
  const errorDeleteTimeEntryText = getErrorText(errorDeleteTimeEntry);

  const deleteModalEmployeeName = entryToDelete ? getEntryEmployeeName(entryToDelete) : "—";
  const deleteModalDateLabel = entryToDelete ? formatDateLabel(entryToDelete.date) : "—";
  const deleteModalIn = entryToDelete ? formatTime(entryToDelete.check_in) : "—";
  const deleteModalOut =
    entryToDelete && entryToDelete.check_out === null ? "Abierto" : formatTime(entryToDelete?.check_out);

  return (
    <div className="hr-manager-tab hr-manager-time-tab">
      {/* Header card */}
      <div className="hr-manager-time-header-card">
        <div className="hr-manager-time-header-left">
          <p className="hr-manager-time-kicker">Fichajes</p>
          <h2 className="hr-manager-time-title">Fichajes del equipo</h2>
          <p className="hr-manager-time-subtitle">
            Revisa fichajes, horas trabajadas y patrones de jornada por rango de fechas.
          </p>
        </div>

        <div className="hr-manager-time-header-right">
          <div className="hr-manager-time-pill">
            <CalendarDays className="hr-manager-time-pill-icon" aria-hidden="true" />
            <span className="hr-manager-time-pill-text">{rangeLabel}</span>
          </div>
          <div className="hr-manager-time-pill hr-manager-time-pill--secondary">
            <Users className="hr-manager-time-pill-icon" aria-hidden="true" />
            <span className="hr-manager-time-pill-text">{employeeLabel}</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="hr-manager-time-filters-card">
        <div className="hr-manager-time-filter-row">
          <div className="hr-filter-select">
            <label>Empleado (opcional)</label>
            <select
              value={selectedEmployeeId}
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
              disabled={loadingEmployeesList}
            >
              <option value="">
                {loadingEmployeesList ? "Cargando empleados..." : "Todos los empleados"}
              </option>
              {employeesList.map((employee) => {
                const user = employee.user || {};
                const fullName =
                  ((user.first_name || "") + " " + (user.last_name || "")).trim() ||
                  user.username ||
                  user.email ||
                  "Sin nombre";

                return (
                  <option key={employee.id} value={String(employee.id)}>
                    {fullName}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="hr-filter-dates">
            <div className="hr-filter-date-field">
              <label>Desde</label>
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>

            <div className="hr-filter-date-field">
              <label>Hasta</label>
              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
          </div>

          <button
            type="button"
            className="hr-button hr-button--primary"
            onClick={handleSearch}
            disabled={loadingManagerTimeEntries || !startDate || !endDate}
          >
            {loadingManagerTimeEntries ? "Cargando..." : "Aplicar filtros"}
          </button>
        </div>

        {(errorEmployeesText ||
          errorManagerTimeEntriesText ||
          errorUpdateTimeEntryText ||
          errorDeleteTimeEntryText) && (
          <div className="hr-error-banner">
            {errorEmployeesText && <span>Error al cargar empleados: {errorEmployeesText}</span>}
            {errorManagerTimeEntriesText && (
              <span>Error al cargar fichajes del equipo: {errorManagerTimeEntriesText}</span>
            )}
            {errorUpdateTimeEntryText && (
              <span>Error al actualizar fichaje: {errorUpdateTimeEntryText}</span>
            )}
            {errorDeleteTimeEntryText && (
              <span>Error al eliminar fichaje: {errorDeleteTimeEntryText}</span>
            )}
          </div>
        )}
      </div>

      {/* Métricas + tabla */}
      {hasSearched && (
        <>
          <div className="hr-manager-time-metrics">
            <div className="hr-metric-card hr-metric-card--primary">
              <div className="hr-metric-top">
                <span className="hr-metric-label">Horas totales</span>
                <Clock className="hr-metric-icon" aria-hidden="true" />
              </div>
              <div className="hr-metric-value">
                {safeTotalHours.toFixed(2)}
                <span className="hr-metric-unit">h</span>
              </div>
              <p className="hr-metric-helper">Suma de todas las horas del rango.</p>
            </div>

            <div className="hr-metric-card">
              <div className="hr-metric-top">
                <span className="hr-metric-label">Días con fichajes</span>
                <CalendarDays className="hr-metric-icon" aria-hidden="true" />
              </div>
              <div className="hr-metric-value">
                {totalDays}
                <span className="hr-metric-unit">días</span>
              </div>
              <p className="hr-metric-helper">Días en los que hubo actividad registrada.</p>
            </div>

            <div className="hr-metric-card">
              <div className="hr-metric-top">
                <span className="hr-metric-label">Media por día</span>
                <Clock className="hr-metric-icon" aria-hidden="true" />
              </div>
              <div className="hr-metric-value">
                {safeAvgPerDay.toFixed(2)}
                <span className="hr-metric-unit">h/día</span>
              </div>
              <p className="hr-metric-helper">Promedio por día con fichaje.</p>
            </div>
          </div>

          <div className="hr-manager-time-table-card">
            <div className="hr-manager-time-table-header">
              <div>
                <h3 className="hr-manager-time-table-title">Fichajes en el rango seleccionado</h3>
                <p className="hr-manager-time-table-subtitle">
                  {selectedEmployee ? (
                    <>
                      Empleado: <strong>{employeeLabel}</strong>
                    </>
                  ) : (
                    <>
                      Mostrando fichajes de <strong>todos los empleados</strong>.
                    </>
                  )}
                </p>
              </div>

              <div className="hr-manager-time-table-pill">
                <Users className="hr-manager-time-table-pill-icon" aria-hidden="true" />
                <span>{sortedTimeEntries.length} fichajes</span>
              </div>
            </div>

            {loadingManagerTimeEntries ? (
              <div className="hr-manager-time-empty">Cargando fichajes...</div>
            ) : sortedTimeEntries.length === 0 ? (
              <div className="hr-manager-time-empty">No hay fichajes registrados en este periodo.</div>
            ) : (
              <div className="hr-time-history-table-wrapper">
                <table className="hr-table">
                  <thead>
                    <tr>
                      <th>Empleado</th>
                      <th>Fecha</th>
                      <th>Entrada</th>
                      <th>Salida</th>
                      <th>Horas</th>
                      <th>Estado</th>
                      <th style={{ textAlign: "right" }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTimeEntries.map((entry) => {
                      const isOpen = entry.check_out === null;
                      const duration = getDurationHours(entry);
                      const isEditing = editingEntryId === entry.id;

                      return (
                        <tr key={entry.id}>
                          <td>{getEntryEmployeeName(entry)}</td>

                          <td>
                            {isEditing ? (
                              <input
                                className="hr-inline-input"
                                type="date"
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                              />
                            ) : (
                              formatDateLabel(entry.date)
                            )}
                          </td>

                          <td>
                            {isEditing ? (
                              <input
                                className="hr-inline-input"
                                type="time"
                                value={editCheckInTime}
                                onChange={(e) => setEditCheckInTime(e.target.value)}
                              />
                            ) : (
                              formatTime(entry.check_in)
                            )}
                          </td>

                          <td>
                            {isEditing ? (
                              <input
                                className="hr-inline-input"
                                type="time"
                                value={editCheckOutTime}
                                onChange={(e) => setEditCheckOutTime(e.target.value)}
                              />
                            ) : isOpen ? (
                              "Abierto"
                            ) : (
                              formatTime(entry.check_out)
                            )}
                          </td>

                          <td>{duration != null ? duration.toFixed(2) : "—"}</td>

                          <td>
                            <span
                              className={
                                isOpen ? "hr-badge hr-badge--open" : "hr-badge hr-badge--closed"
                              }
                            >
                              {isOpen ? "En curso" : "Cerrado"}
                            </span>
                          </td>

                          <td style={{ textAlign: "right" }}>
                            {isEditing ? (
                              <div className="hr-manager-time-actions">
                                <button
                                  type="button"
                                  className="hr-button hr-button--primary"
                                  onClick={() => handleSaveEdit(entry.id)}
                                  disabled={loadingUpdateTimeEntry}
                                >
                                  {loadingUpdateTimeEntry ? "Guardando..." : "Guardar"}
                                </button>

                                <button
                                  type="button"
                                  className="hr-button hr-button--ghost"
                                  onClick={cancelEdit}
                                  disabled={loadingUpdateTimeEntry}
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <div className="hr-manager-time-actions">
                                <button
                                  type="button"
                                  className="hr-icon-button"
                                  onClick={() => startEditEntry(entry)}
                                  aria-label="Editar fichaje"
                                  title="Editar fichaje"
                                >
                                  <Pencil width={16} height={16} aria-hidden="true" />
                                </button>

                                <button
                                  type="button"
                                  className="hr-icon-button hr-icon-button--danger"
                                  onClick={() => openDeleteModal(entry)}
                                  disabled={loadingDeleteTimeEntry}
                                  aria-label="Eliminar fichaje"
                                  title="Eliminar fichaje"
                                >
                                  <Trash2 width={16} height={16} aria-hidden="true" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!hasSearched && (
        <div className="hr-manager-time-helper">
          <Users className="hr-manager-time-helper-icon" aria-hidden="true" />
          <span>
            Elige un rango y, si quieres, filtra por empleado. Por defecto se muestra el mes actual.
          </span>
        </div>
      )}

      {/* MODAL ELIMINAR (global ui modal, sin X) */}
      {deleteModalOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ui-delete-time-title"
            aria-describedby="ui-delete-time-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3 id="ui-delete-time-title">Eliminar fichaje</h3>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help" id="ui-delete-time-desc">
                ¿Seguro que quieres eliminar este fichaje?
                <div className="ui-delete-subhelp">
                  <strong>{deleteModalEmployeeName}</strong> · {deleteModalDateLabel} · {deleteModalIn} →{" "}
                  {deleteModalOut}
                </div>
                <div className="ui-delete-subhelp">Esta acción no se puede deshacer.</div>
              </div>

              {errorDeleteTimeEntryText ? (
                <div className="ui-modal-error" role="alert" aria-live="polite">
                  {errorDeleteTimeEntryText}
                </div>
              ) : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteModal}
                disabled={loadingDeleteTimeEntry}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDelete}
                disabled={loadingDeleteTimeEntry}
              >
                {loadingDeleteTimeEntry ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrManagerTimeTab;